import React from 'react';
import PlayIcon from '../icons/Play';
import styled from 'styled-components';
import tw from 'twin.macro';
import breakpoints from 'utils/breakpoints';
import GalleryVideo from './GalleryVideo';

const Grid = styled.div.attrs(({ className }) => ({
  className: `w-full ${className}`,
}))`
  display: grid;
  grid-template-columns: repeat(${({ columnsSm }) => columnsSm}, 1fr);
  grid-gap: ${({ gridGap }) => (gridGap ? gridGap : '0.1rem')};

  @media ${breakpoints.lg} {
    grid-template-columns: repeat(${({ columnsLg }) => columnsLg}, 1fr);
  }
`;

const ItemContainer = styled.div`
  ${tw`relative overflow-auto flex flex-col items-center`}
  ${({ isList }) => isList && 'height: 60vw'};
  &:hover {
    opacity: 0.3;
  }
`;

const Figure = styled.figure`
  ${tw`flex justify-center w-full relative h-24 sm:h-24 md:h-48 lg:h-64`}

  ${({ isList }) => isList && 'height: 80%;'}
  max-height: 20rem;
`;

const WhiteCircle = styled.span`
  ${tw`bg-white rounded-full opacity-75 flex items-center justify-center h-16 w-16 lg:w-1/2
    uppercase text-action-blue font-trade-gothic-20 text-lg lg:h-1/2
  `}
  max-width: 4rem;
  max-height: 4rem;
`;

const WhiteSquare = styled.span`
  ${tw`bg-white opacity-75 w-full h-full flex items-center justify-center uppercase text-mattamy-blue font-trade-gothic-20 text-3xl`}
`;

const BlueBar = styled.span.attrs({
  className: 'absolute bottom bg-action-blue left-0 right-0 mx-auto',
})`
  bottom: -0.125rem;
  height: 0.375rem;
  width: 80%;
`;

const Text = styled.p`
  ${tw`font-franklin text-mattamy-gray text-sm italic pt-1 font-light text-center border-aqua text-left z-10`}
  width: 90%;
  margin-top: -0.1875rem;
  border-top-width: 0.375rem;
`;

const Tour = styled.div`
  & iframe {
    min-height: 100% !important;
  }
`;

const getOverlay = (media, i, others, thumbs, isList) => {
  if (i === thumbs.length - 1 && others >= 1) {
    return (
      <div className='absolute inset-0 flex items-center justify-center'>
        <WhiteSquare>
          <span className='-mb-1'>+{others}</span>
        </WhiteSquare>
      </div>
    );
  } else if (media.type === 'video' && !isList) {
    return (
      <div className='absolute inset-0 flex items-center justify-center'>
        <WhiteCircle>
          <PlayIcon className='h-6 w-6 text-action-blue' />
        </WhiteCircle>
      </div>
    );
  } else if (media.type === 'tour' && !isList) {
    return (
      <div className='absolute inset-0 flex items-center justify-center'>
        <WhiteCircle>
          <span className='-mb-1'>Tour</span>
        </WhiteCircle>
      </div>
    );
  }
  return null;
};

const smallBreakpoints = ['xs', 'sm', 'md'];

const Gallery = (props) => {
  const {
    media,
    thumbnailItems = 8,
    columnsLg = 4,
    columnsSm = 3,
    gridGap,
    breakpoint,
    mediaType,
    selectedImage,
  } = props;
  const others = media && media.length - (thumbnailItems || media.length);
  const thumbs = media && media.slice(0, thumbnailItems || media.length);

  const isSmall = smallBreakpoints.includes(breakpoint);
  const isList = columnsSm === 1 && isSmall;

  return (
    <Grid
      columnsLg={columnsLg}
      columnsSm={columnsSm}
      gridGap={gridGap}
      className={props.className}
    >
      {thumbs.map((media, i) => (
        <ItemContainer isList={isList} key={i}>
          <Figure
            key={media.thumbnail + i}
            isList={isList}
            onClick={(e) => {
              e.stopPropagation();
              props.onItemClick && props.onItemClick(media);
            }}
          >
            {isList && media.type === 'video' && <GalleryVideo media={media} />}
            {isList && (media.type === 'image' || mediaType === 'image') && (
              <img
                src={media.src || media.imageUrl || media.thumbnail}
                alt={media.alt || media.imageAlt}
                className='block h-full w-full object-cover object-center'
              />
            )}
            {media.flag && (
              <div className='absolute inset-x-0 top-0 flex'>
                <span className='w-auto bg-white text-mattamy-gray uppercase font-trade-gothic-20'>
                  {media.flag}
                </span>
              </div>
            )}
            {isList && media.type === 'tour' && (
              <Tour
                className='w-full h-full'
                dangerouslySetInnerHTML={{ __html: media.src }}
              />
            )}
            {!isList && (
              <img
                className='block h-full w-full object-cover object-center'
                src={media.thumbnail}
                alt={media.alt}
              />
            )}
            {getOverlay(media, i, others, thumbs, isList)}
            {props.type && props.type === 'modal' ? (
              <BlueBar />
            ) : (
              i === selectedImage &&
              !isSmall && (
                <>
                  <div className='absolute inset-0 flex items-center justify-center bg-white opacity-75' />
                  <BlueBar />
                </>
              )
            )}
          </Figure>
          {props.type && props.type === 'modal' ? (
            <Text>{media.description}</Text>
          ) : (
            isSmall &&
            isList && <Text>{media.description || media.imageCaption}</Text>
          )}
        </ItemContainer>
      ))}
    </Grid>
  );
};

export default Gallery;
